import { useEffect, useState } from "react";
import { Plus, Trash, PencilLine, Eye, EyeSlash, FloppyDisk, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, errMsg, useApi, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT } from "../lib/i18n";
import { PageHeader, GlassCard, Modal, Field, Loader, Empty, Table, Badge } from "../components/kit";

function ExamForm({ classes, onClose, onSaved }) {
  const { t } = useT();
  const [f, setF] = useState({ name: "", class_id: classes[0]?.id || "", date: "", max_marks: 100 });
  const [subjects, setSubjects] = useState(classes[0]?.subjects || []);
  useEffect(() => {
    setSubjects(classes.find((c) => c.id === f.class_id)?.subjects || []);
  }, [f.class_id, classes]);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/exams", { name: f.name, class_id: f.class_id, date: f.date, subjects: subjects.map((s) => ({ name: s, max_marks: Number(f.max_marks) })) });
      toast.success("Exam created");
      onSaved();
    } catch (er) {
      toast.error(errMsg(er));
    }
  };
  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title="Create exam" testid="exam-form-dialog">
      <form onSubmit={submit} className="space-y-4">
        <Field label={t("Name")}><input className="field" required placeholder="Half Yearly" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="exam-name-input" /></Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label={t("Class")}>
            <select className="field" value={f.class_id} onChange={(e) => setF({ ...f, class_id: e.target.value })} data-testid="exam-class-select">
              {classes.map((c) => <option key={c.id} value={c.id}>{c.name} {c.section}</option>)}
            </select>
          </Field>
          <Field label={t("Date")}><input type="date" className="field" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="exam-date-input" /></Field>
        </div>
        <Field label="Max marks per subject"><input type="number" min={1} className="field" value={f.max_marks} onChange={(e) => setF({ ...f, max_marks: e.target.value })} data-testid="exam-max-input" /></Field>
        <div className="flex flex-wrap gap-2">{subjects.map((s) => <span key={s} className="rounded-full bg-white/5 border border-white/10 px-3 py-1 text-xs">{s}</span>)}</div>
        <button className="btn-primary w-full" data-testid="exam-submit-btn">{t("Create")}</button>
      </form>
    </Modal>
  );
}

function MarksEntry({ exam, onBack }) {
  const { t } = useT();
  const { data, loading } = useApi(`/exams/${exam.id}/marks`);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setRows(data?.students || []);
  }, [data]);
  const setScore = (sid, sub, v) => setRows(rows.map((r) => (r.id === sid ? { ...r, scores: { ...r.scores, [sub]: v === "" ? null : Number(v) } } : r)));
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/exams/${exam.id}/marks`, { entries: rows.map((r) => ({ student_id: r.id, scores: r.scores })) });
      toast.success("Marks saved");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <button className="btn-ghost mb-6" onClick={onBack} data-testid="marks-back-btn"><ArrowLeft size={16} /> {t("Exams")}</button>
      <PageHeader eyebrow={`${exam.class_name} · ${fmtDate(exam.date)}`} title={`${t("Enter marks")} — ${exam.name}`}
        actions={<button className="btn-primary" disabled={busy} onClick={save} data-testid="save-marks-btn"><FloppyDisk size={16} /> {t("Save")}</button>} />
      <GlassCard className="p-4 sm:p-6">
        {loading ? <Loader /> : !rows.length ? <Empty /> : (
          <Table testid="marks-table">
            <thead><tr><th>{t("Roll no")}</th><th>{t("Name")}</th>{exam.subjects.map((s) => <th key={s.name}>{s.name} <span className="text-zinc-600">/{s.max_marks}</span></th>)}<th>{t("Total")}</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>{r.roll_no}</td><td className="font-semibold text-white">{r.name}</td>
                  {exam.subjects.map((s) => (
                    <td key={s.name}><input type="number" min={0} max={s.max_marks} className="field !w-20 !py-1.5 !px-2 text-center" value={r.scores[s.name] ?? ""}
                      onChange={(e) => setScore(r.id, s.name, e.target.value)} data-testid={`mark-${r.id}-${s.name}`} /></td>
                  ))}
                  <td className="font-semibold text-blue-300">{Object.values(r.scores).reduce((a, v) => a + (v || 0), 0)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </GlassCard>
    </div>
  );
}

export default function Exams() {
  const { user } = useAuth();
  const { t } = useT();
  const isAdmin = user.role === "admin";
  const { data: classes } = useApi("/classes");
  const { data, loading, reload } = useApi("/exams");
  const [form, setForm] = useState(false);
  const [entry, setEntry] = useState(null);
  const act = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (entry) return <MarksEntry exam={entry} onBack={() => { setEntry(null); reload(); }} />;
  return (
    <div>
      <PageHeader eyebrow="Assessment" title={t("Exams & Marks")} subtitle="Create exams, enter marks, then publish results to parents and students."
        actions={isAdmin && <button className="btn-primary" onClick={() => setForm(true)} disabled={!classes?.length} data-testid="create-exam-btn"><Plus size={16} weight="bold" /> Create exam</button>} />
      <GlassCard className="p-4 sm:p-6">
        {loading ? <Loader /> : !data?.length ? <Empty /> : (
          <Table testid="exams-table">
            <thead><tr><th>{t("Name")}</th><th>{t("Class")}</th><th>{t("Date")}</th><th>Subjects</th><th>Marks entered</th><th>{t("Status")}</th><th></th></tr></thead>
            <tbody>
              {data.map((e) => (
                <tr key={e.id} data-testid={`exam-row-${e.id}`}>
                  <td className="font-semibold text-white">{e.name}</td><td>{e.class_name}</td><td>{fmtDate(e.date)}</td><td>{e.subjects.length}</td><td>{e.marks_entered}</td>
                  <td><Badge status={e.published ? "published" : "draft"}>{t(e.published ? "Published" : "Draft")}</Badge></td>
                  <td className="text-right space-x-1">
                    <button className="btn-ghost !py-1.5 !px-3" onClick={() => setEntry(e)} data-testid={`exam-marks-${e.id}`}><PencilLine size={15} /> {t("Enter marks")}</button>
                    {isAdmin && (
                      <>
                        <button className="btn-ghost !py-1.5 !px-3" onClick={() => act(() => api.patch(`/exams/${e.id}`, { published: !e.published }), e.published ? "Unpublished" : "Published")} data-testid={`exam-publish-${e.id}`}>
                          {e.published ? <EyeSlash size={15} /> : <Eye size={15} />} {e.published ? "Unpublish" : t("Publish")}
                        </button>
                        <button className="btn-ghost !p-2 !text-rose-300" onClick={() => window.confirm("Delete exam and its marks?") && act(() => api.delete(`/exams/${e.id}`), "Deleted")} data-testid={`exam-delete-${e.id}`}><Trash size={15} /></button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </GlassCard>
      {form && <ExamForm classes={classes} onClose={() => setForm(false)} onSaved={() => { setForm(false); reload(); }} />}
    </div>
  );
}
